import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router'
import { ArrowLeft, BarChart3, Download, Eye, ExternalLink, Loader2, Play, Printer, Save, Table2 } from 'lucide-react'
import { toast } from 'sonner'
import { flexibleReportService, type FlexibleReportDefinition, type FlexibleReportFieldMapping } from '../services/flexibleReportService'
import { exportToExcel } from './reportUtils'

type PreviewData = {
  fields: string[]
  rows: any[]
}

type RunResult = {
  id: number
  title: string
  dateField: string | null
  fieldMappings: FlexibleReportFieldMapping[]
  fields: string[]
  rows: any[]
}

const DEFAULT_RANGE = {
  startDate: '2000-01-01 00:00:00',
  endDate: '2100-01-01 00:00:00',
}

const syncFieldMappings = (fields: string[], currentMappings: FlexibleReportFieldMapping[]) => {
  return fields.map((field) => {
    const existing = currentMappings.find((item) => item.field === field)
    return { field, label: existing?.label || field }
  })
}

const formatDateTime = (value?: string) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('tr-TR')
}

type FlexibleReportsBuilderProps = {
  standalone?: boolean
}

const buildExportRows = (fields: string[], rows: any[], fieldLabels: Map<string, string>) => {
  return rows.map((row) => {
    const nextRow: Record<string, any> = {}
    fields.forEach((field) => {
      nextRow[fieldLabels.get(field) || field] = row[field] ?? '-'
    })
    return nextRow
  })
}

const buildPrintableHtml = (title: string, fields: string[], rows: any[], fieldLabels: Map<string, string>) => {
  const headCells = fields.map((field) => `<th>${fieldLabels.get(field) || field}</th>`).join('')
  const bodyRows = rows.length
    ? rows.map((row) => `<tr>${fields.map((field) => `<td>${String(row[field] ?? '-')}</td>`).join('')}</tr>`).join('')
    : `<tr><td colspan="${Math.max(fields.length, 1)}">Bu kriterlerde veri bulunamadi</td></tr>`

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${title}</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 24px; color: #111827; }
      h1 { font-size: 20px; margin: 0 0 6px; }
      p { color: #4b5563; margin: 0 0 20px; }
      table { width: 100%; border-collapse: collapse; font-size: 12px; }
      th, td { border: 1px solid #d1d5db; padding: 8px 10px; text-align: left; vertical-align: top; }
      thead th { background: #f3f4f6; }
      tbody tr:nth-child(even) { background: #f9fafb; }
    </style>
  </head>
  <body>
    <h1>${title}</h1>
    <p>Toplam ${rows.length} satir</p>
    <table>
      <thead><tr>${headCells}</tr></thead>
      <tbody>${bodyRows}</tbody>
    </table>
  </body>
</html>`
}

export function FlexibleReportsBuilder({ standalone = false }: FlexibleReportsBuilderProps) {
  const [reports, setReports] = useState<FlexibleReportDefinition[]>([])
  const [reportsLoading, setReportsLoading] = useState(false)
  const [title, setTitle] = useState('')
  const [sqlQuery, setSqlQuery] = useState('SELECT shipment_no, pickup_date, final_price FROM shipments ORDER BY id DESC')
  const [fieldMappings, setFieldMappings] = useState<FlexibleReportFieldMapping[]>([])
  const [dateField, setDateField] = useState<string | null>(null)
  const [previewData, setPreviewData] = useState<PreviewData | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [listOpen, setListOpen] = useState(true)
  const [runResult, setRunResult] = useState<RunResult | null>(null)
  const [running, setRunning] = useState(false)
  const [pendingRunReport, setPendingRunReport] = useState<FlexibleReportDefinition | null>(null)
  const [runStartDate, setRunStartDate] = useState('')
  const [runEndDate, setRunEndDate] = useState('')
  const listRef = useRef<HTMLDivElement>(null)

  const fieldLabels = useMemo(() => {
    const entries = new Map<string, string>()
    fieldMappings.forEach((item) => entries.set(item.field, item.label || item.field))
    return entries
  }, [fieldMappings])

  const loadReports = async () => {
    try {
      setReportsLoading(true)
      const response: any = await flexibleReportService.list()
      setReports(Array.isArray(response?.data) ? response.data : [])
    } catch (error: any) {
      console.error('Flexible reports load failed:', error)
      toast.error(error?.message || 'Esnek rapor listesi alınamadı')
    } finally {
      setReportsLoading(false)
    }
  }

  useEffect(() => {
    void loadReports()
  }, [])

  const resetForm = () => {
    setEditingId(null)
    setTitle('')
    setSqlQuery('SELECT shipment_no, pickup_date, final_price FROM shipments ORDER BY id DESC')
    setFieldMappings([])
    setDateField(null)
    setPreviewData(null)
    setPreviewModalOpen(false)
  }

  const handlePreview = async () => {
    if (!sqlQuery.trim()) {
      toast.error('SQL kodu girin')
      return
    }

    try {
      setPreviewLoading(true)
      const response: any = await flexibleReportService.preview({
        sqlQuery,
        dateField,
        startDate: dateField ? DEFAULT_RANGE.startDate : null,
        endDate: dateField ? DEFAULT_RANGE.endDate : null,
      })
      const payload = response?.data || {}
      const fields = Array.isArray(payload.fields) ? payload.fields : []
      const rows = Array.isArray(payload.rows) ? payload.rows : []
      setPreviewData({ fields, rows })
      setPreviewModalOpen(true)
      setFieldMappings((current) => syncFieldMappings(fields, current))
      if (dateField && !fields.includes(dateField)) {
        setDateField(null)
      }
      toast.success(`SQL çalıştı, ${rows.length} satır önizleme alındı`)
    } catch (error: any) {
      console.error('Flexible report preview failed:', error)
      toast.error(error?.message || 'SQL çalıştırılamadı')
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Rapor başlığı zorunlu')
      return
    }
    if (!sqlQuery.trim()) {
      toast.error('SQL kodu zorunlu')
      return
    }
    if (!fieldMappings.length) {
      toast.error('Önce SQL Kodunu Çalıştır ile alanları oluşturun')
      return
    }

    try {
      setSaving(true)
      const payload = {
        title: title.trim(),
        sqlQuery: sqlQuery.trim(),
        dateField,
        fieldMappings,
      }
      if (editingId) {
        await flexibleReportService.update(editingId, payload)
        toast.success('Rapor güncellendi')
      } else {
        await flexibleReportService.create(payload)
        toast.success('Rapor kaydedildi')
      }
      await loadReports()
      setListOpen(true)
      listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } catch (error: any) {
      console.error('Flexible report save failed:', error)
      toast.error(error?.message || 'Rapor kaydedilemedi')
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (report: FlexibleReportDefinition) => {
    setEditingId(report.id)
    setTitle(report.title)
    setSqlQuery(report.sqlQuery)
    setFieldMappings(report.fieldMappings || [])
    setDateField(report.dateField || null)
    setPreviewData(null)
    setPreviewModalOpen(false)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const executeReport = async (report: FlexibleReportDefinition, startDate?: string, endDate?: string) => {
    try {
      setRunning(true)
      const response: any = await flexibleReportService.run(report.id, {
        startDate: startDate || null,
        endDate: endDate || null,
      })
      setRunResult(response?.data || null)
      toast.success('Rapor çalıştırıldı')
    } catch (error: any) {
      console.error('Flexible report run failed:', error)
      toast.error(error?.message || 'Rapor çalıştırılamadı')
    } finally {
      setRunning(false)
    }
  }

  const handleRun = async (report: FlexibleReportDefinition) => {
    if (report.dateField) {
      setPendingRunReport(report)
      if (!runStartDate && !runEndDate) {
        const now = new Date()
        const start = new Date(now)
        start.setDate(now.getDate() - 30)
        setRunStartDate(start.toISOString().slice(0, 16))
        setRunEndDate(now.toISOString().slice(0, 16))
      }
      return
    }
    await executeReport(report)
  }

  const displayedFields = useMemo(() => {
    if (!runResult) return [] as string[]
    const baseFields = Array.isArray(runResult.fields) ? runResult.fields : []
    if (!baseFields.length && runResult.rows[0]) {
      return Object.keys(runResult.rows[0])
    }
    return baseFields
  }, [runResult])

  const handleExportRunResult = () => {
    if (!runResult) return
    const exportRows = buildExportRows(displayedFields, runResult.rows || [], fieldLabels)
    if (!exportRows.length) {
      toast.error('Disa aktarilacak veri bulunamadi')
      return
    }
    const safeTitle = runResult.title.replace(/[^a-zA-Z0-9-_]/g, '_')
    exportToExcel(exportRows, `${safeTitle}_${new Date().toISOString().slice(0, 10)}`, runResult.title)
    toast.success('Rapor Excel olarak disa aktarildi')
  }

  const handlePrintRunResult = () => {
    if (!runResult) return
    const printWindow = window.open('', '_blank', 'width=1200,height=800')
    if (!printWindow) {
      toast.error('Yazdirma penceresi acilamadi')
      return
    }
    printWindow.document.open()
    printWindow.document.write(buildPrintableHtml(runResult.title, displayedFields, runResult.rows || [], fieldLabels))
    printWindow.document.close()
    printWindow.focus()
    printWindow.print()
  }

  return (
    <div className="space-y-6">
      {standalone && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-white p-5 shadow-sm">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Ozel Rapor Olusturucu</h1>
            <p className="mt-1 text-sm text-slate-500">Rapor tanimlayin, calistirin, yazdirin ve disa aktarın.</p>
          </div>
          <Link to="/reports?tab=custom" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
            <ArrowLeft className="h-4 w-4" />
            Raporlara Don
          </Link>
        </div>
      )}

      <div className="rounded-xl border bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Esnek Rapor Tanımı</h3>
            <p className="mt-1 text-sm text-gray-500">SELECT veya WITH ile başlayan SQL yazın. Tarih aralığı kullanacaksanız ilgili tarih alanını sorgu sonucunda seçin.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {!standalone && (
              <Link to="/flexible-reports" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                <ExternalLink className="h-4 w-4" />
                Ayrı Sayfada Ac
              </Link>
            )}
            <button type="button" onClick={() => { setListOpen(true); listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }) }} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              <Table2 className="h-4 w-4" />
              Listede Göster
            </button>
            <button type="button" onClick={resetForm} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              Yeni Rapor
            </button>
          </div>
        </div>

        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">Rapor Başlığı</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ornek: Gunluk teslimat ozet raporu" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500" />
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Tarih aralıklı çalıştırmak için sorgu sonucunda bir tarih kolonu döndürün ve aşağıda o kolonu `Tarih Alanı` olarak işaretleyin.
          </div>
        </div>

        <div className="mt-4">
          <label className="mb-2 block text-sm font-medium text-slate-700">SQL Kodu</label>
          <textarea value={sqlQuery} onChange={(e) => setSqlQuery(e.target.value)} rows={10} spellCheck={false} className="w-full rounded-lg border border-slate-300 px-3 py-3 font-mono text-sm outline-none focus:border-blue-500" />
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" onClick={() => void handlePreview()} disabled={previewLoading} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
            {previewLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            SQL Kodunu Çalıştır
          </button>
          <button type="button" onClick={() => void handleSave()} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {editingId ? 'Raporu Güncelle' : 'Raporu Kaydet'}
          </button>
        </div>
      </div>

      {previewData && (
        <div className="space-y-6 rounded-xl border bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
            <h3 className="text-lg font-semibold text-gray-900">Alan Eşleştirme</h3>
            <p className="mt-1 text-sm text-gray-500">Kolon etiketlerini burada belirleyin. Tarih aralıklı filtrelenecekse ilgili alanı seçin.</p>
            </div>
            <button type="button" onClick={() => setPreviewModalOpen(true)} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              <Eye className="h-4 w-4" />
              Onizlemeyi Ac
            </button>
          </div>
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-slate-600">
                <tr>
                  <th className="px-4 py-3 font-medium">Alan</th>
                  <th className="px-4 py-3 font-medium">Etiket</th>
                  <th className="px-4 py-3 font-medium">Tarih Alanı</th>
                </tr>
              </thead>
              <tbody>
                {fieldMappings.map((mapping) => (
                  <tr key={mapping.field} className="border-t border-slate-200">
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{mapping.field}</td>
                    <td className="px-4 py-3">
                      <input
                        value={mapping.label}
                        onChange={(e) => setFieldMappings((current) => current.map((item) => item.field === mapping.field ? { ...item, label: e.target.value } : item))}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-blue-500"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <label className="inline-flex items-center gap-2 text-slate-700">
                        <input type="radio" name="dateField" checked={dateField === mapping.field} onChange={() => setDateField(mapping.field)} />
                        Seç
                      </label>
                      {dateField === mapping.field && (
                        <button type="button" onClick={() => setDateField(null)} className="ml-3 text-xs text-rose-600 hover:text-rose-700">Temizle</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div ref={listRef} className="rounded-xl border bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Kayitli Esnek Raporlar</h3>
            <p className="mt-1 text-sm text-gray-500">Cok sayida raporu tablo halinde yonetin ve calistirin.</p>
          </div>
          <button type="button" onClick={() => setListOpen((current) => !current)} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
            <Eye className="h-4 w-4" />
            {listOpen ? 'Listeyi Gizle' : 'Listeyi Goster'}
          </button>
        </div>

        {listOpen && (
          <div className="mt-5 overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-slate-600">
                <tr>
                  <th className="px-4 py-3 font-medium">Baslik</th>
                  <th className="px-4 py-3 font-medium">Tarih Alani</th>
                  <th className="px-4 py-3 font-medium">Alan Sayisi</th>
                  <th className="px-4 py-3 font-medium">Guncellendi</th>
                  <th className="px-4 py-3 font-medium">Islem</th>
                </tr>
              </thead>
              <tbody>
                {reportsLoading ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Yukleniyor...</td></tr>
                ) : reports.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Kayitli esnek rapor yok</td></tr>
                ) : reports.map((report) => (
                  <tr key={report.id} className="border-t border-slate-200 even:bg-slate-50/50">
                    <td className="px-4 py-3 font-medium text-slate-900">{report.title}</td>
                    <td className="px-4 py-3">{report.dateField || '-'}</td>
                    <td className="px-4 py-3">{report.fieldMappings?.length || 0}</td>
                    <td className="px-4 py-3">{formatDateTime(report.updatedAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={() => handleEdit(report)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">Duzenle</button>
                        <button type="button" onClick={() => void handleRun(report)} disabled={running} className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                          {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BarChart3 className="h-3.5 w-3.5" />}
                          Goster
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {runResult && (
        <div className="rounded-xl border bg-white p-6 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{runResult.title}</h3>
              <p className="mt-1 text-sm text-gray-500">Toplam {runResult.rows.length} satir</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={handlePrintRunResult} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                <Printer className="h-4 w-4" />
                Yazdir
              </button>
              <button type="button" onClick={handleExportRunResult} className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700">
                <Download className="h-4 w-4" />
                Disa Aktar
              </button>
            </div>
          </div>
          <div className="mt-5 overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-slate-600">
                <tr>
                  {displayedFields.map((field) => <th key={field} className="px-4 py-3 font-medium">{fieldLabels.get(field) || field}</th>)}
                </tr>
              </thead>
              <tbody>
                {runResult.rows.length === 0 ? (
                  <tr><td colSpan={Math.max(displayedFields.length, 1)} className="px-4 py-8 text-center text-slate-500">Bu kriterlerde veri bulunamadi</td></tr>
                ) : runResult.rows.map((row, index) => (
                  <tr key={index} className="border-t border-slate-200 even:bg-slate-50/50">
                    {displayedFields.map((field) => <td key={field} className="px-4 py-3">{String(row[field] ?? '-')}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {pendingRunReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900">Tarih Araligi Girin</h3>
            <p className="mt-1 text-sm text-slate-500">{pendingRunReport.title} raporu icin ilk ve son tarih-saati birlikte girin.</p>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Ilk tarih saat</label>
                <input type="datetime-local" value={runStartDate} onChange={(e) => setRunStartDate(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-blue-500" />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Son tarih saat</label>
                <input type="datetime-local" value={runEndDate} onChange={(e) => setRunEndDate(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-blue-500" />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setPendingRunReport(null)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Vazgec</button>
              <button
                type="button"
                onClick={async () => {
                  if (!runStartDate || !runEndDate) {
                    toast.error('Iki tarih-saat alani da zorunlu')
                    return
                  }
                  const activeReport = pendingRunReport
                  setPendingRunReport(null)
                  await executeReport(activeReport, runStartDate, runEndDate)
                }}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                <BarChart3 className="h-4 w-4" />
                Goster
              </button>
            </div>
          </div>
        </div>
      )}

      {previewData && previewModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Onizleme Sonucu</h3>
                <p className="mt-1 text-sm text-slate-500">Toplam {previewData.rows.length} satir. Ilk 20 kayit gosteriliyor.</p>
              </div>
              <button type="button" onClick={() => setPreviewModalOpen(false)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
                Kapat
              </button>
            </div>
            <div className="overflow-auto px-6 py-5">
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-left text-slate-600">
                    <tr>
                      {previewData.fields.map((field) => <th key={field} className="px-4 py-3 font-medium">{field}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.rows.slice(0, 20).map((row, index) => (
                      <tr key={index} className="border-t border-slate-200 even:bg-slate-50/50">
                        {previewData.fields.map((field) => <td key={field} className="px-4 py-3">{String(row[field] ?? '-')}</td>)}
                      </tr>
                    ))}
                    {previewData.rows.length === 0 && (
                      <tr>
                        <td colSpan={Math.max(previewData.fields.length, 1)} className="px-4 py-8 text-center text-slate-500">Sorgu calisti, ancak veri donmedi.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
